import { ref, watch } from 'vue';
import { defineStore } from 'pinia';
import { axiosIncidentInstance, makeRequest } from './middleware';
import { useOrganizationStore } from './organization';
import type { IncidentType } from '@/gen/types/IncidentType';
import type { IncidentCatalogDto } from '@/gen/types/IncidentCatalogDto';
import type { IncidentCommentCreateRequestDto } from '@/gen/types/IncidentCommentCreateRequestDto';

import type { IncidentSubmissionResponseDto } from '@/gen/types/IncidentSubmissionResponseDto';
import type { IncidentCreateResponseDto } from '@/gen/types/IncidentCreateResponseDto';
import type { ResponseBaseOfIncidentDetailsDtoBase } from '@/gen/types/ResponseBaseOfIncidentDetailsDtoBase';
import type { IncidentsResponse } from '@/gen/types/IncidentsResponse';
import type { GetAdminAllIncidentsQueryParams } from '@/gen/types/GetAdminAllIncidents';
import type { IncidentListItemDto } from '@/gen/types/IncidentListItemDto';
import type { ResponseBaseOfIncidentCommentGetResponseDto } from '@/gen/types/ResponseBaseOfIncidentCommentGetResponseDto';
import type { ResponseBaseOfNocMasterDetailDto } from '@/gen/types/ResponseBaseOfNocMasterDetailDto';
import type { NocMasterDetailDto } from '@/gen/types/NocMasterDetailDto';
import type { IncidentDetailsDtoBase } from '@/gen/types/IncidentDetailsDtoBase';
import type { ResponseBaseOfIncidentCommentCreateResponseDto } from '@/gen/types/ResponseBaseOfIncidentCommentCreateResponseDto';
import type { AttachmentCreateDto } from '@/gen/types/AttachmentCreateDto';
import type { AffectedOrg } from '@/gen/types/AffectedOrg';
import type { ResponseBaseOfGetAffectedOrgsResponseDto } from '@/gen/types/ResponseBaseOfGetAffectedOrgsResponseDto';
import type { ShareResult } from '@/gen/types/ShareResult';
import type { ShareWithAffectedRequestDto } from '@/gen/types/ShareWithAffectedRequestDto';
import type { ResponseBaseOfShareWithAffectedResponseDto } from '@/gen/types/ResponseBaseOfShareWithAffectedResponseDto';
import type { LinkInternalResult } from '@/gen/types/LinkInternalResult';
import type { LinkInternalRequestDto } from '@/gen/types/LinkInternalRequestDto';
import type { ResponseBaseOfLinkInternalResponseDto } from '@/gen/types/ResponseBaseOfLinkInternalResponseDto';
import type { ReplyResult } from '@/gen/types/ReplyResult';
import type { ReplyToAffectedRequestDto } from '@/gen/types/ReplyToAffectedRequestDto';
import type { ResponseBaseOfReplyToAffectedResponseDto } from '@/gen/types/ResponseBaseOfReplyToAffectedResponseDto';
import { getIncidentCreateEndpoint } from '@/incidents/forms/registry';
import { defaultIncidentAudience, type IncidentAudience } from '@/incidents/forms/audience';
import { normalizeJiraWikiText } from '@/lib/jira-wiki';
import { humanizeIdentifier, isIdentifierLikeString } from '@/lib/string-format';
import { randomUuid } from '@/lib/uuid';

/**
 * A create-incident response. The internal/NOC create endpoints return an async submission
 * ({@link IncidentSubmissionResponseDto}, polled to completion); the customer create endpoints
 * resolve synchronously and return the created incident ({@link IncidentCreateResponseDto}).
 * Discriminate with `'submissionId' in response`.
 */
export type IncidentCreateResult = IncidentSubmissionResponseDto | IncidentCreateResponseDto;
import { useStorage } from '@vueuse/core';
import type { Option } from './store-interfaces';

export interface IncidentFilterState {
    status: Option[];
    priorities: Option[];
    requestTypes: Option[];
    incidentId?: string;
    summarySearch?: string;
    dateTimeFrom: string;
    dateTimeTo?: string;
}

const INCIDENT_CATALOG_CACHE_MS = 5 * 60 * 1000;
const INCIDENT_CATALOG_ENDPOINT = '/incidents/catalog';

export type ApiResponse<T> = {
    statusCode: number;
    data: T;
};

export type IncidentCatalogCategoryType = {
    id: IncidentType;
    name: string;
    description: string;
    iconKey: string;
};

export type IncidentCatalogCategory = {
    id: string;
    name: string;
    description: string;
    types: IncidentCatalogCategoryType[];
};

type IncidentGroupUiMetadata = {
    name: string;
    description: string;
    /**
     * Optional override used when the audience is 'customer'. The internal `description` field
     * mentions subcategories employees can submit (e.g. cybersecurity, third-party); customers
     * see a filtered list of types and need helper text that matches what they can actually
     * report (ITT-583 bug #2).
     */
    customerDescription?: string;
};

type IncidentTypeUiMetadata = {
    name: string;
    description: string;
    iconKey: string;
};

const INCIDENT_GROUP_UI_METADATA: Record<string, IncidentGroupUiMetadata> = {
    SecurityIncident: {
        name: 'Security Incident',
        description: 'Incidents related to physical, cyber, and third-party security events.',
        customerDescription: 'Report unauthorized physical access or other physical security concerns at our sites.',
    },
    HealthSafetyEnvironmentIncident: {
        name: 'Health, Safety & Environment Incident',
        description: 'Incidents impacting people, safety, environment, or regulatory environmental controls.',
    },
    ComplianceQualityIncident: {
        name: 'Compliance & Quality Incident',
        description: 'Incidents tied to service quality, compliance obligations, and contractual commitments.',
        customerDescription: 'Concerns about your service quality or our contractual commitments to you.',
    },
    OperationalInfrastructureIncident: {
        name: 'Operational / Infrastructure Incident',
        description: 'Incidents affecting critical infrastructure operations, capacity, and service continuity.',
        customerDescription: 'Disruptions to power, cooling, network, or facility systems affecting your services.',
    },
    Observations: {
        name: 'Observations',
        description: 'Positive or actionable observations identified before they escalate into incidents.',
    },
};

const INCIDENT_TYPE_UI_METADATA: Record<IncidentType, IncidentTypeUiMetadata> = {
    ActionableObservation: {
        name: 'Actionable observations',
        description: 'Observed conditions or behaviors requiring a follow-up action.',
        iconKey: 'actionable-observation',
    },
    PositiveObservation: {
        name: 'Positive observations',
        description: 'Positive findings that reinforce safe and effective behaviors.',
        iconKey: 'positive-observation',
    },
    WasteManagement: {
        name: 'Waste Management',
        description: 'Events related to waste handling, classification, or disposal process failures.',
        iconKey: 'waste-management',
    },
    FireExplosion: {
        name: 'Fire / Explosion',
        description: 'Events involving fire, smoke, combustion risk, or explosion hazards.',
        iconKey: 'fire-explosion',
    },
    ChemicalSpillEmissions: {
        name: 'Chemical spill / Emissions',
        description: 'Accidental release, spill, or emission of hazardous substances.',
        iconKey: 'chemical-spill-emissions',
    },
    PeopleIncident: {
        name: 'People incident',
        description: 'Incidents affecting personnel health, injury, welfare, or safety.',
        iconKey: 'people-incident',
    },
    NetworkInfrastructure: {
        name: 'Network infrastructure',
        description: 'Network backbone, routing, switching, or connectivity incidents.',
        iconKey: 'network-infrastructure',
    },
    FacilityInfrastructure: {
        name: 'Facility infrastructure',
        description: 'Building or facility systems incidents impacting operations.',
        iconKey: 'facility-infrastructure',
    },
    CapacityPerformance: {
        name: 'Capacity & Performance',
        description: 'Performance degradation or capacity threshold incidents.',
        iconKey: 'capacity-performance',
    },
    ChangeOrConfigurationError: {
        name: 'Change or Configuration error',
        description: 'Incidents caused by change rollout or configuration mistakes.',
        iconKey: 'change-or-configuration-error',
    },
    PhysicalInfrastructure: {
        name: 'Physical infrastructure',
        description: 'Physical asset incidents affecting core infrastructure integrity.',
        iconKey: 'physical-infrastructure',
    },
    CoolingHVAC: {
        name: 'Cooling / HVAC',
        description: 'Cooling and HVAC incidents affecting thermal stability.',
        iconKey: 'cooling-hvac',
    },
    PowerElectricalSystems: {
        name: 'Power & Electrical systems',
        description: 'Power delivery and electrical systems incidents.',
        iconKey: 'power-electrical-systems',
    },
    CustomerSatisfaction: {
        name: 'Customer satisfaction',
        description: 'Incidents with direct or potential customer experience impact.',
        iconKey: 'customer-satisfaction',
    },
    LegalRegulatory: {
        name: 'Legal / Regulatory',
        description: 'Incidents involving legal obligations or regulatory non-compliance.',
        iconKey: 'legal-regulatory',
    },
    PolicyCompliance: {
        name: 'Policy compliance',
        description: 'Internal policy deviations and compliance control incidents.',
        iconKey: 'policy-compliance',
    },
    SupplierPerformance: {
        name: 'Supplier performance',
        description: 'Incidents linked to supplier delivery, quality, or support performance.',
        iconKey: 'supplier-performance',
    },
    ServiceQualityContractualDeviation: {
        name: 'Service quality & Contractual Obligation',
        description: 'Service quality incidents and contractual deviation events.',
        iconKey: 'service-quality-contractual-deviation',
    },
    ITSystemsNetwork: {
        name: 'IT systems & Network',
        description: 'IT system outages, degradation, or network service interruptions.',
        iconKey: 'it-systems-network',
    },
    ThirdParty: {
        name: 'Third Party',
        description: 'Incidents introduced by or impacting third-party dependencies.',
        iconKey: 'third-party',
    },
    InformationSecurityCybersecurity: {
        name: 'Information & Cyber Security',
        description: 'Security incidents affecting confidentiality, integrity, or availability of information assets.',
        iconKey: 'information-security-cybersecurity',
    },
    PhysicalSecurity: {
        name: 'Physical Security',
        description: 'Incidents involving unauthorized physical access or onsite threats.',
        iconKey: 'physical-security',
    },
};

export function getIncidentTypeTitle(incidentType: IncidentType | null | undefined) {
    if (!incidentType) return 'Create an incident';

    const metadata = INCIDENT_TYPE_UI_METADATA[incidentType];
    if (metadata?.name) return metadata.name;

    return humanizeIdentifier(incidentType, { casing: 'title' });
}

/**
 * Resolve an incident type's icon key from the canonical INCIDENT_TYPE_UI_METADATA lookup.
 * The table is keyed by every IncidentType, so a valid type always resolves. The optional
 * chaining only guards against a type string outside the union (BE/FE drift); an unmapped
 * type returns '' (a visibly missing icon) rather than a silently-wrong derived key.
 */
export function typeToIconKey(incidentType: IncidentType | string | null | undefined): string {
    return INCIDENT_TYPE_UI_METADATA[incidentType as IncidentType]?.iconKey ?? '';
}

export function getIncidentTypeTitleFromIconKey(iconKey: string | null | undefined) {
    if (!iconKey) return '';

    const metadata = Object.values(INCIDENT_TYPE_UI_METADATA).find((entry) => entry.iconKey === iconKey);
    if (metadata?.name) return metadata.name;

    return humanizeIdentifier(iconKey, { casing: 'title' });
}

export function formatIncidentTypeLabel(label: string | null | undefined, fallbackIconKey?: string | null) {
    const trimmedLabel = label?.trim() ?? '';
    if (!trimmedLabel) {
        return getIncidentTypeTitleFromIconKey(fallbackIconKey);
    }

    if (isIdentifierLikeString(trimmedLabel, { requireLeadingLetter: true })) {
        return getIncidentTypeTitle(trimmedLabel as IncidentType);
    }

    return trimmedLabel;
}

function isIncidentCatalog(payload: unknown): payload is IncidentCatalogDto {
    if (!payload || typeof payload !== 'object') return false;

    const catalog = payload as Partial<IncidentCatalogDto>;
    return Array.isArray(catalog.groups) && Array.isArray(catalog.requestTypes) && Array.isArray(catalog.fields);
}

function extractIncidentCatalog(payload: unknown): IncidentCatalogDto | null {
    if (isIncidentCatalog(payload)) return payload;
    if (!payload || typeof payload !== 'object') return null;
    if (!('data' in payload)) return null;

    const wrappedData = (payload as { data?: unknown }).data;
    return isIncidentCatalog(wrappedData) ? wrappedData : null;
}

function fileToBase64(file: File): Promise<string> {
    return new Promise((resolve, reject) => {
        const reader = new FileReader();

        reader.onload = () => {
            if (typeof reader.result !== 'string') {
                reject(new Error('Failed to convert attachment to base64.'));
                return;
            }

            const [, base64Content = ''] = reader.result.split(',', 2);
            resolve(base64Content);
        };

        reader.onerror = () => {
            reject(reader.error ?? new Error('Failed to read attachment file.'));
        };

        reader.readAsDataURL(file);
    });
}

async function fileToAttachmentCreateDto(file: File): Promise<AttachmentCreateDto> {
    return {
        fileName: file.name,
        contentType: file.type || 'application/octet-stream',
        base64Content: await fileToBase64(file),
    };
}

function resolveGroupDescription(metadata: IncidentGroupUiMetadata | undefined, audience: IncidentAudience): string {
    if (audience === 'customer' && metadata?.customerDescription) return metadata.customerDescription;
    return metadata?.description || '';
}

function mapRequestTypeToCategoryType(
    requestType: NonNullable<IncidentCatalogDto['requestTypes']>[number],
    incidentType: IncidentType,
): IncidentCatalogCategoryType {
    const metadata = INCIDENT_TYPE_UI_METADATA[incidentType];

    return {
        id: incidentType,
        name: metadata?.name || humanizeIdentifier(incidentType, { casing: 'title' }),
        description: metadata?.description || requestType.description || '',
        iconKey: metadata?.iconKey || incidentType,
    };
}

export function mapIncidentCatalogToCategories(
    catalog: IncidentCatalogDto | null | undefined,
    audience: IncidentAudience = defaultIncidentAudience,
): IncidentCatalogCategory[] {
    if (!catalog) return [];

    const requestTypesByGroupKey = new Map<string, IncidentCatalogCategoryType[]>();

    for (const requestType of catalog.requestTypes ?? []) {
        const incidentType = requestType.incidentType;
        if (!incidentType) continue;
        const categoryType = mapRequestTypeToCategoryType(requestType, incidentType);
        for (const groupKey of requestType.groupKeys ?? []) {
            if (!groupKey) continue;

            const existing = requestTypesByGroupKey.get(groupKey) ?? [];
            if (!existing.some((type) => type.id === categoryType.id)) {
                existing.push(categoryType);
            }
            requestTypesByGroupKey.set(groupKey, existing);
        }
    }

    const categories: IncidentCatalogCategory[] = [];
    const knownGroupKeys = new Set<string>();

    for (const group of catalog.groups ?? []) {
        const metadata = INCIDENT_GROUP_UI_METADATA[group.groupKey];
        knownGroupKeys.add(group.groupKey);
        categories.push({
            id: group.groupKey,
            name: metadata?.name || humanizeIdentifier(group.groupKey, { casing: 'title' }) || group.groupKey,
            description: resolveGroupDescription(metadata, audience),
            types: requestTypesByGroupKey.get(group.groupKey) ?? [],
        });
    }

    for (const [groupKey, types] of requestTypesByGroupKey) {
        if (knownGroupKeys.has(groupKey)) continue;
        const metadata = INCIDENT_GROUP_UI_METADATA[groupKey];
        categories.push({
            id: groupKey,
            name: metadata?.name || humanizeIdentifier(groupKey, { casing: 'title' }) || groupKey,
            description: resolveGroupDescription(metadata, audience),
            types,
        });
    }

    return categories;
}

export interface IncidentFilterData {
    statuses?: string[];
    priorities?: string[];
    requestTypes?: string[];
    incidentId?: string;
    summary?: string;
    dateTimeFrom?: string;
    dateTimeTo?: string;
}

/**
 * Link-candidate picker source label (ITDEVELOP-2920). Drives the modal's empty-state copy:
 * - `category-match`: rows match the master's primary groupKey within ±24h.
 * - `fallback-no-category-match`: category-filtered call returned 0; this list is the same window
 *   minus Observations.
 * - `fallback-empty`: both calls returned 0 — render the typed-key fallback hint.
 */
export type LinkCandidateSource = 'category-match' | 'fallback-no-category-match' | 'fallback-empty';

export interface LinkCandidatesResult {
    rows: IncidentListItemDto[];
    source: LinkCandidateSource;
}

export interface ListLinkCandidatesOptions {
    masterKey: string;
    masterGroupKey: string | null;
    masterCreatedAt: string;
    pageSize?: number;
}

export interface IncidentLocation {
    scope: string;
    sites: string[];
}

export interface IncidentDto {
    incidentId: string;
    type: string;
    status: string;
    priority: string;
    occurredAt: string;
    createdAt: string;
    updatedAt: string;
    summary: string;
    description: string;
    location: IncidentLocation;
    reportedByFunction: string;
    thirdPartyInvolved: string[];
    potentialImpactOnAnotherIncidentCategory: string;
    attachments: unknown[];
    externalReference: string;
    reporterName?: string | null;
}

export type GetIncidentsResponse = {
    nextPageToken?: string;
    count: number;
    pagesize: number;
    totalCount: number;
    statusCode: number;
    data: IncidentDto[];
};

export type IncidentListRequestBody = {
    maxResult: number;
    nextPageToken: string | null;
    statuses?: string[];
    priorities?: string[];
    requestTypes?: string[];
    reference?: string;
    title?: string;
    dateTimeFrom?: string;
    dateTimeTo?: string;
};

/**
 * Builds the canonical incident list payload used both for the API request
 * and for determining whether an identical incident list request is already
 * in flight.
 */
export function buildIncidentListRequestBody(
    filter: IncidentFilterData,
    nextPageToken: string | null,
    maxResults: number,
): IncidentListRequestBody {
    const body: IncidentListRequestBody = {
        maxResult: maxResults,
        nextPageToken,
        statuses: filter.statuses?.length ? filter.statuses : undefined,
        priorities: filter.priorities?.length ? filter.priorities : undefined,
        requestTypes: filter.requestTypes?.length ? filter.requestTypes : undefined,
        reference: filter.incidentId?.trim() || undefined,
        title: filter.summary?.trim() || undefined,
    };

    if (filter.dateTimeFrom && filter.dateTimeFrom.trim() !== '') {
        body.dateTimeFrom = filter.dateTimeFrom.includes('T')
            ? filter.dateTimeFrom
            : `${filter.dateTimeFrom}T00:00:00Z`;
    }

    if (filter.dateTimeTo && filter.dateTimeTo.trim() !== '') {
        body.dateTimeTo = filter.dateTimeTo.includes('T') ? filter.dateTimeTo : `${filter.dateTimeTo}T23:59:59Z`;
    }

    return body;
}

/**
 * Produces the stable key used to dedupe identical in-flight incident list
 * requests. Matching keys mean we can reuse the existing promise instead of
 * issuing the same backend request twice.
 */
export function getIncidentListInFlightRequestKey(body: IncidentListRequestBody): string {
    return JSON.stringify(body);
}

export const useIncidentStore = defineStore('incident', () => {
    const organizationStore = useOrganizationStore();

    const incidentCatalog = ref<IncidentCatalogDto | null>(null);
    const incidentCatalogLoading = ref(false);
    const incidentCatalogError = ref<string | null>(null);
    const incidentCatalogFetchedAt = ref<number | null>(null);

    const persistedFilter = useStorage<IncidentFilterState>('incident-filters', {
        status: [],
        priorities: [],
        requestTypes: [],
        incidentId: '',
        summarySearch: '',
        dateTimeFrom: '',
        dateTimeTo: '',
    });

    let incidentCatalogController: AbortController | null = null;
    let incidentCatalogRequestId = 0;
    let inFlightIncidentListRequest: {
        key: string;
        promise: Promise<GetIncidentsResponse>;
    } | null = null;

    function resetFilters() {
        persistedFilter.value = {
            status: [],
            priorities: [],
            requestTypes: [],
            incidentId: '',
            summarySearch: '',
            dateTimeFrom: '',
            dateTimeTo: '',
        };
    }

    function isIncidentCatalogStale() {
        if (!incidentCatalogFetchedAt.value) return true;
        return Date.now() - incidentCatalogFetchedAt.value > INCIDENT_CATALOG_CACHE_MS;
    }

    function abortIncidentCatalogRequest() {
        incidentCatalogController?.abort();
        incidentCatalogController = null;
    }

    function waitForUsersOrganization() {
        return new Promise<void>((resolve) => {
            const stop = watch(
                () => organizationStore.usersOrganization?.organization_id,
                (id) => {
                    if (id !== undefined) {
                        stop();
                        resolve();
                    }
                },
                { immediate: true },
            );
        });
    }

    function resetIncidentCatalog() {
        abortIncidentCatalogRequest();
        incidentCatalog.value = null;
        incidentCatalogError.value = null;
        incidentCatalogFetchedAt.value = null;
        incidentCatalogLoading.value = false;
    }

    async function fetchIncidentCatalog(options: { force?: boolean } = {}) {
        if (!options.force && incidentCatalog.value && !isIncidentCatalogStale()) {
            return incidentCatalog.value;
        }

        // Reload of /incidents/create can mount the selector before the organization
        // store finishes populating; rather than bailing with an error that no one
        // ever clears (ITDEVELOP-2855), wait for the org to land and proceed.
        if (organizationStore.usersOrganization?.organization_id === undefined) {
            await waitForUsersOrganization();
        }

        abortIncidentCatalogRequest();
        incidentCatalogController = new AbortController();
        const requestId = ++incidentCatalogRequestId;

        incidentCatalogLoading.value = true;
        incidentCatalogError.value = null;

        try {
            const response = await makeRequest<IncidentCatalogDto | ApiResponse<IncidentCatalogDto>>(
                'GET',
                INCIDENT_CATALOG_ENDPOINT,
                axiosIncidentInstance,
                undefined,
                { signal: incidentCatalogController.signal, throwOnError: true, skipToast: true },
            );
            const catalog = extractIncidentCatalog(response?.data);
            if (!catalog) {
                throw new Error('Empty incident catalog response.');
            }

            if (requestId === incidentCatalogRequestId) {
                incidentCatalog.value = catalog;
                incidentCatalogFetchedAt.value = Date.now();
            }
            return catalog;
        } catch (error) {
            const isCanceled =
                (error && typeof error === 'object' && 'code' in error && error.code === 'ERR_CANCELED') ||
                (error && typeof error === 'object' && 'name' in error && error.name === 'CanceledError') ||
                (error && typeof error === 'object' && 'name' in error && error.name === 'AbortError');
            if (!isCanceled && requestId === incidentCatalogRequestId) {
                incidentCatalogError.value = 'Unable to load incident categories.';
            }
            return null;
        } finally {
            if (requestId === incidentCatalogRequestId) {
                incidentCatalogLoading.value = false;
            }
        }
    }

    async function getIncidentCatalog(options: { force?: boolean } = {}) {
        return fetchIncidentCatalog(options);
    }

    async function createRequest(
        incidentType: IncidentType,
        payload: Record<string, unknown>,
        options: { audience?: IncidentAudience } = {},
    ) {
        const endpoint = getIncidentCreateEndpoint(incidentType, options.audience);

        // The internal/NOC endpoints are asynchronous (202 + submissionId, polled to completion);
        // the customer endpoints resolve synchronously and return the created incident directly
        // (IncidentCreateResponseDto). Callers discriminate on the response shape.
        const response = await makeRequest<IncidentCreateResult>('POST', endpoint, axiosIncidentInstance, payload, {
            throwOnError: true,
            headers: {
                'X-Idempotency-Key': randomUuid(),
            },
        });

        return response.data;
    }

    async function getIncidentById(incidentId: string) {
        const response = await makeRequest<ResponseBaseOfIncidentDetailsDtoBase>(
            'GET',
            `/incidents/${encodeURIComponent(incidentId)}`,
            axiosIncidentInstance,
            undefined,
            {
                throwOnError: true,
            },
        );

        return response.data;
    }

    async function getIncidentSubmission(submissionId: string) {
        const response = await makeRequest<IncidentSubmissionResponseDto>(
            'GET',
            `/incidents/submissions/${encodeURIComponent(submissionId)}`,
            axiosIncidentInstance,
            undefined,
            {
                throwOnError: true,
            },
        );

        return response.data;
    }

    async function getIncidentComments(incidentId: string, options: { start?: number; limit?: number } = {}) {
        const start = options.start ?? 0;
        const limit = options.limit ?? 50;
        const query = new URLSearchParams({
            start: String(start),
            limit: String(limit),
        });

        const response = await makeRequest<ResponseBaseOfIncidentCommentGetResponseDto>(
            'GET',
            `/incidents/${encodeURIComponent(incidentId)}/comments?${query.toString()}`,
            axiosIncidentInstance,
            undefined,
            {
                throwOnError: true,
            },
        );

        return response.data;
    }

    async function postIncidentComment(incidentId: string, comment: string, files: File[]) {
        const payload: IncidentCommentCreateRequestDto = {
            comment: normalizeJiraWikiText(comment),
        };

        if (files?.length) {
            payload.attachments = await Promise.all(files.map(fileToAttachmentCreateDto));
        }

        const response = await makeRequest<ResponseBaseOfIncidentCommentCreateResponseDto>(
            'POST',
            `/incidents/${encodeURIComponent(incidentId)}/comments`,
            axiosIncidentInstance,
            payload,
            {
                throwOnError: true,
            },
        );

        return response.data;
    }

    /**
     * NOC reply on a single communication ticket (ITDEVELOP-2792): POST /admin/incidents/{key}/comments.
     * Mirrors postIncidentComment but on the admin-scoped path and carries the explicit visibility flag.
     * `isPublic=true` posts a public reply the customer sees on their ticket; `isPublic=false` posts an
     * internal NOC-only note. Authority derives from the communication-admin-writer role on the endpoint
     * (the per-org access check is bypassed server-side), so the NOC can reply on customer-owned children.
     */
    async function postNocIncidentComment(incidentId: string, comment: string, files: File[], isPublic: boolean) {
        const payload: IncidentCommentCreateRequestDto = {
            comment: normalizeJiraWikiText(comment),
            isPublic,
        };

        if (files?.length) {
            payload.attachments = await Promise.all(files.map(fileToAttachmentCreateDto));
        }

        const response = await makeRequest<ResponseBaseOfIncidentCommentCreateResponseDto>(
            'POST',
            `/admin/incidents/${encodeURIComponent(incidentId)}/comments`,
            axiosIncidentInstance,
            payload,
            {
                throwOnError: true,
            },
        );

        return response.data;
    }

    /**
     * Broadcast one public comment to a set of the master's linked Communication children
     * (ITDEVELOP-2694): POST /admin/incidents/{master}/reply-to-affected. Each selected child gets the
     * same public comment on its own ticket (the customer sees it there, never on the master or each
     * other's). Returns the per-child result so the caller can report partial failures. NOC/admin scope.
     */
    async function replyToAffected(
        masterKey: string,
        childKeys: string[],
        comment: string,
        files: File[],
    ): Promise<ReplyResult[]> {
        const payload: ReplyToAffectedRequestDto = {
            childKeys,
            comment: normalizeJiraWikiText(comment),
        };

        if (files?.length) {
            payload.attachments = await Promise.all(files.map(fileToAttachmentCreateDto));
        }

        const response = await makeRequest<ResponseBaseOfReplyToAffectedResponseDto>(
            'POST',
            `/admin/incidents/${encodeURIComponent(masterKey)}/reply-to-affected`,
            axiosIncidentInstance,
            payload,
            { throwOnError: true },
        );

        return response.data?.data?.results ?? [];
    }

    async function getIncidentAttachment(incidentId: string, attachmentId: string) {
        const response = await makeRequest<Blob>(
            'GET',
            `/incidents/${encodeURIComponent(incidentId)}/attachments/${encodeURIComponent(attachmentId)}`,
            axiosIncidentInstance,
            undefined,
            {
                responseType: 'blob',
                throwOnError: true,
            },
        );

        return response.data;
    }

    async function getIncidentAttachmentThumbnail(incidentId: string, attachmentId: string) {
        const response = await makeRequest<Blob>(
            'GET',
            `/incidents/${encodeURIComponent(incidentId)}/attachments/${encodeURIComponent(attachmentId)}/thumbnail`,
            axiosIncidentInstance,
            undefined,
            {
                responseType: 'blob',
                throwOnError: true,
            },
        );

        return response.data;
    }

    /**
     * Returns incidents for the given filter set and reuses an identical
     * in-flight request when the same list query is triggered concurrently.
     */
    async function getIncidents(filter: IncidentFilterData, nextPageToken: string | null, maxResults: number) {
        const body = buildIncidentListRequestBody(filter, nextPageToken, maxResults);
        const requestKey = getIncidentListInFlightRequestKey(body);

        if (inFlightIncidentListRequest?.key === requestKey) {
            return inFlightIncidentListRequest.promise;
        }

        const requestPromise = makeRequest<GetIncidentsResponse>('POST', `/incidents`, axiosIncidentInstance, body)
            .then((response) => response.data)
            .finally(() => {
                if (inFlightIncidentListRequest?.key === requestKey) {
                    inFlightIncidentListRequest = null;
                }
            });

        inFlightIncidentListRequest = {
            key: requestKey,
            promise: requestPromise,
        };

        return requestPromise;
    }

    /**
     * NOC incident-list overview (ITDEVELOP-2677 / ITDEVELOP-2768): GET /admin/all-incidents
     * returns the incidents visible to the caller (NOC/admin scope), scoped server-side by the
     * caller's accessible sites + X-Organization-Id. Requires the noc-admin/admin role.
     * Paginated via pageSize/nextPageToken. Filtering is applied **server-side** (the list is
     * paginated, so client-side filtering would only cover loaded rows): the same IncidentFilter
     * facets as the customer list flow through to the backend (ITDEVELOP-2767) as repeated query
     * keys (OR within a facet, AND across facets). Param names mirror the customer filter state.
     */
    async function getNocIncidents(filter: IncidentFilterData = {}, pageSize?: number, nextPageToken?: string | null) {
        const params: GetAdminAllIncidentsQueryParams = {};
        if (pageSize) params.pageSize = pageSize;
        if (nextPageToken) params.nextPageToken = nextPageToken;
        if (filter.statuses?.length) params.statuses = filter.statuses;
        if (filter.priorities?.length) params.priorities = filter.priorities;
        if (filter.requestTypes?.length) params.requestTypes = filter.requestTypes;

        const reference = filter.incidentId?.trim();
        if (reference) params.incidentId = reference;

        const title = filter.summary?.trim();
        if (title) params.summary = title;

        if (filter.dateTimeFrom && filter.dateTimeFrom.trim() !== '') {
            params.dateTimeFrom = filter.dateTimeFrom.includes('T')
                ? filter.dateTimeFrom
                : `${filter.dateTimeFrom}T00:00:00Z`;
        }
        if (filter.dateTimeTo && filter.dateTimeTo.trim() !== '') {
            params.dateTimeTo = filter.dateTimeTo.includes('T') ? filter.dateTimeTo : `${filter.dateTimeTo}T23:59:59Z`;
        }

        const response = await makeRequest<IncidentsResponse>(
            'GET',
            '/admin/all-incidents',
            axiosIncidentInstance,
            undefined,
            // List<string> query params bind from repeated keys (statuses=a&statuses=b);
            // indexes:null stops axios emitting bracketed keys (statuses[]=a) which ASP.NET
            // would not bind to the array param.
            { throwOnError: true, params, paramsSerializer: { indexes: null } },
        );

        return {
            items: response.data?.data ?? [],
            nextPageToken: response.data?.nextPageToken ?? null,
        };
    }

    /**
     * Lists candidate tickets for the "Link existing ticket" modal (ITDEVELOP-2920). Pool =
     * customer-raised tickets created ±24h from {@link ListLinkCandidatesOptions.masterCreatedAt},
     * within the NOC caller's site scope, excluding NOC-originated masters via
     * `incidentSources=External`. Category is matched by walking the loaded incident catalog to find
     * the request-type names whose primary `groupKey` equals the master's `masterGroupKey`. If the
     * category-filtered call returns zero rows, falls back to the same window minus the
     * `Observations` group's request types and flags the result accordingly.
     *
     * The catalog walk treats `groupKeys[0]` as canonical (locked by ADR-0026); request types in
     * practice belong to a single group. A null `masterGroupKey` (master without a resolvable group)
     * short-circuits straight into the fallback path.
     */
    async function listLinkCandidates(options: ListLinkCandidatesOptions): Promise<LinkCandidatesResult> {
        const pageSize = options.pageSize ?? 50;
        const windowMs = 24 * 60 * 60 * 1000;
        const masterAt = Date.parse(options.masterCreatedAt);
        if (Number.isNaN(masterAt)) {
            // Defensive: a master without a valid createdAt can't anchor a window. Surface as
            // "fallback-empty" so the modal renders the typed-key-fallback empty state.
            return { rows: [], source: 'fallback-empty' };
        }
        const dateTimeFrom = new Date(masterAt - windowMs).toISOString();
        const dateTimeTo = new Date(masterAt + windowMs).toISOString();

        const catalog = await fetchIncidentCatalog();
        const requestTypesByPrimaryGroup = new Map<string, string[]>();
        for (const requestType of catalog?.requestTypes ?? []) {
            const primary = requestType.groupKeys?.[0];
            const incidentType = requestType.incidentType;
            if (!primary || !incidentType) continue;
            const bucket = requestTypesByPrimaryGroup.get(primary) ?? [];
            if (!bucket.includes(incidentType)) bucket.push(incidentType);
            requestTypesByPrimaryGroup.set(primary, bucket);
        }

        const categoryRequestTypes = options.masterGroupKey
            ? requestTypesByPrimaryGroup.get(options.masterGroupKey) ?? []
            : [];

        // First pass — category-filtered. Only attempt if we have request types to send;
        // an empty filter would return everything (the BE treats empty as no facet).
        if (categoryRequestTypes.length > 0) {
            const categoryRows = await fetchLinkCandidatePage({
                masterKey: options.masterKey,
                requestTypes: categoryRequestTypes,
                dateTimeFrom,
                dateTimeTo,
                pageSize,
            });
            if (categoryRows.length > 0) {
                return { rows: categoryRows, source: 'category-match' };
            }
        }

        // Fallback — same window, all non-Observations request types.
        const fallbackRequestTypes: string[] = [];
        for (const [groupKey, types] of requestTypesByPrimaryGroup) {
            if (groupKey === 'Observations') continue;
            for (const t of types) if (!fallbackRequestTypes.includes(t)) fallbackRequestTypes.push(t);
        }

        const fallbackRows = fallbackRequestTypes.length > 0
            ? await fetchLinkCandidatePage({
                masterKey: options.masterKey,
                requestTypes: fallbackRequestTypes,
                dateTimeFrom,
                dateTimeTo,
                pageSize,
            })
            : [];

        if (fallbackRows.length === 0) {
            return { rows: [], source: 'fallback-empty' };
        }
        return { rows: fallbackRows, source: 'fallback-no-category-match' };
    }

    async function fetchLinkCandidatePage(opts: {
        masterKey: string;
        requestTypes: string[];
        dateTimeFrom: string;
        dateTimeTo: string;
        pageSize: number;
    }): Promise<IncidentListItemDto[]> {
        const params: GetAdminAllIncidentsQueryParams = {
            pageSize: opts.pageSize,
            requestTypes: opts.requestTypes,
            dateTimeFrom: opts.dateTimeFrom,
            dateTimeTo: opts.dateTimeTo,
            incidentSources: ['External'],
        };

        const response = await makeRequest<IncidentsResponse>(
            'GET',
            '/admin/all-incidents',
            axiosIncidentInstance,
            undefined,
            { throwOnError: true, params, paramsSerializer: { indexes: null } },
        );

        const rows = response.data?.data ?? [];
        // Exclude the master itself defensively (the BE filter already excludes Internal-source
        // tickets, but a stale options resolution could let one through).
        const masterKeyUpper = opts.masterKey.toUpperCase();
        return rows.filter((row) => row.incidentId?.toUpperCase() !== masterKeyUpper);
    }

    /**
     * NOC master-incident detail (ITDEVELOP-2693): GET /admin/incidents/{key} returns the master
     * incident, its linked per-organization Communication children, and the master's comments
     * (NocMasterDetailDto) — but only when {key} is actually a master. A non-master key 404s
     * ("requested master incident was not found"), so this doubles as a master-probe and returns
     * null in that case. Any other failure (auth, 5xx, network) is re-thrown so the caller can
     * surface a load error rather than silently falling back. The X-Organization-Id header is
     * injected by axiosIncidentInstance; the endpoint is site-scoped by the caller's role.
     */
    async function getNocIncidentDetail(key: string): Promise<NocMasterDetailDto | null> {
        try {
            const response = await makeRequest<ResponseBaseOfNocMasterDetailDto>(
                'GET',
                `/admin/incidents/${encodeURIComponent(key)}`,
                axiosIncidentInstance,
                undefined,
                { throwOnError: true },
            );

            return response.data?.data ?? null;
        } catch (error) {
            // 404 = "{key} is not a master" — expected; signal with null so callers probe the
            // single-incident path. Re-throw everything else.
            const status = (error as { response?: { status?: number } })?.response?.status;
            if (status === 404) return null;
            throw error;
        }
    }

    /**
     * NOC single-incident detail (ITDEVELOP-2693): GET /admin/incidents/{key}/detail returns one
     * incident's full fields, scoped by the caller's accessible sites (404 when out of scope).
     * This is the NOC path for a child or operational incident — the customer GET /incidents/{key}
     * denies NOC callers ("You do not have access to this incident"). The endpoint is absent from
     * the generated OpenAPI types today, so the response is typed inline; `data` is detail-shaped.
     */
    async function getNocSingleIncidentDetail(
        key: string,
        options: { skipToast?: boolean } = {},
    ): Promise<IncidentDetailsDtoBase | null> {
        // skipToast lets keystroke-driven probes (e.g. the link-modal live preview) handle a
        // 403/400/not-found inline instead of popping the interceptor's global access-denied toast.
        const response = await makeRequest<{ data?: IncidentDetailsDtoBase | null }>(
            'GET',
            `/admin/incidents/${encodeURIComponent(key)}/detail`,
            axiosIncidentInstance,
            undefined,
            { throwOnError: true, skipToast: options.skipToast === true },
        );

        return response.data?.data ?? null;
    }

    /**
     * NOC comment thread for a single incident (ITDEVELOP-2693): GET /admin/incidents/{key}/comments.
     * Used to lazy-load a per-child thread when a child is selected in the linked-tickets rail.
     * Mirrors getIncidentComments but on the admin-scoped path.
     */
    async function getNocIncidentComments(key: string, options: { start?: number; limit?: number } = {}) {
        const start = options.start ?? 0;
        const limit = options.limit ?? 50;
        const query = new URLSearchParams({
            start: String(start),
            limit: String(limit),
        });

        const response = await makeRequest<ResponseBaseOfIncidentCommentGetResponseDto>(
            'GET',
            `/admin/incidents/${encodeURIComponent(key)}/comments?${query.toString()}`,
            axiosIncidentInstance,
            undefined,
            { throwOnError: true },
        );

        return response.data;
    }

    /**
     * NOC attachment thumbnail (ITDEVELOP-2792): GET /admin/incidents/{key}/attachments/{id}/thumbnail.
     * Admin-scoped counterpart of getIncidentAttachmentThumbnail — the customer route runs a per-org
     * access check that denies NOC callers on customer-owned tickets. Used to render inline comment
     * images in the NOC thread.
     */
    async function getNocIncidentAttachmentThumbnail(key: string, attachmentId: string) {
        const response = await makeRequest<Blob>(
            'GET',
            `/admin/incidents/${encodeURIComponent(key)}/attachments/${encodeURIComponent(attachmentId)}/thumbnail`,
            axiosIncidentInstance,
            undefined,
            {
                responseType: 'blob',
                throwOnError: true,
            },
        );

        return response.data;
    }

    /**
     * Affected organizations for a master incident (ITDEVELOP-2717): GET /incidents/{key}/affected-orgs.
     * Derived server-side from the master's location footprint (every org holding ACL on those
     * locations), regardless of whether a Communication child is already linked — callers
     * cross-reference the master's children to mark already-shared orgs. NOC-scoped endpoint.
     */
    async function getAffectedOrgs(masterKey: string): Promise<AffectedOrg[]> {
        const response = await makeRequest<ResponseBaseOfGetAffectedOrgsResponseDto>(
            'GET',
            `/incidents/${encodeURIComponent(masterKey)}/affected-orgs`,
            axiosIncidentInstance,
            undefined,
            { throwOnError: true },
        );

        return response.data?.data?.orgs ?? [];
    }

    /**
     * Share a master incident with affected organizations (ITDEVELOP-2717):
     * POST /incidents/{key}/share-with-affected. Creates one Communication child per selected org
     * (Model A, hidden behind the endpoint). Idempotent per org — an already-shared org reports
     * success without creating a duplicate; an org outside the affected set reports a failure.
     * Returns the per-org results for the caller to surface.
     */
    async function shareWithAffected(masterKey: string, organizationIds: string[]): Promise<ShareResult[]> {
        const payload: ShareWithAffectedRequestDto = { organizationIds };

        const response = await makeRequest<ResponseBaseOfShareWithAffectedResponseDto>(
            'POST',
            `/incidents/${encodeURIComponent(masterKey)}/share-with-affected`,
            axiosIncidentInstance,
            payload,
            { throwOnError: true },
        );

        // The ResponseBase envelope can report an error in its body while HTTP itself returned 200
        // (observed in dev: upstream Jira misconfig → `{statusCode:502, detail:"…", data:null}`).
        // Without this check, callers fall through to `data?.results ?? []` and the UI reads zero
        // failures as full success.
        const envelope = response.data;
        const envelopeStatus = Number(envelope?.statusCode);
        if (Number.isFinite(envelopeStatus) && envelopeStatus >= 400) {
            throw new Error(envelope?.detail || envelope?.message || 'Failed to share with affected organizations');
        }

        return envelope?.data?.results ?? [];
    }

    /**
     * Manually link existing tickets as Communication children of a master (ITDEVELOP-2678):
     * POST /incidents/{key}/link-internal. Creates the master→child issuelink per key — no work-type
     * conversion, no existence pre-check (an unknown key fails individually in the results). The
     * escape hatch for orgs the location-footprint derivation misses.
     */
    async function linkInternalTickets(
        masterKey: string,
        internalIncidentKeys: string[],
    ): Promise<LinkInternalResult[]> {
        const payload: LinkInternalRequestDto = { internalIncidentKeys };

        const response = await makeRequest<ResponseBaseOfLinkInternalResponseDto>(
            'POST',
            `/incidents/${encodeURIComponent(masterKey)}/link-internal`,
            axiosIncidentInstance,
            payload,
            { throwOnError: true },
        );

        return response.data?.data?.results ?? [];
    }

    return {
        getIncidentCatalog,
        fetchIncidentCatalog,
        abortIncidentCatalogRequest,
        resetIncidentCatalog,
        incidentCatalog,
        incidentCatalogLoading,
        incidentCatalogError,
        isIncidentCatalogStale,
        createRequest,
        getIncidentById,
        getIncidentSubmission,
        getIncidentComments,
        postIncidentComment,
        postNocIncidentComment,
        getIncidents,
        getNocIncidents,
        listLinkCandidates,
        getNocIncidentDetail,
        getNocIncidentAttachmentThumbnail,
        getNocSingleIncidentDetail,
        getNocIncidentComments,
        getIncidentAttachment,
        getIncidentAttachmentThumbnail,
        getAffectedOrgs,
        shareWithAffected,
        linkInternalTickets,
        replyToAffected,
        persistedFilter,
        resetFilters,
    };
});
